import { Link, useNavigate } from "@tanstack/solid-router";
import { useQuery } from "@tanstack/solid-query";
import { For, Show, createEffect } from "solid-js";

import { apiRequest } from "~/lib/api";
import { isAuthenticated, logout } from "~/lib/auth";

type Item = {
  workspace_id: string;
  workspace_key: string;
  workspace_name: string;
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
  partner_id: string;
  role_key: string;
  role_level: string;
  read_only: boolean;
  archived: boolean;
  membership_status: string;
  is_partner_admin: boolean;
};

export function WorkspacesPage() {
  const navigate = useNavigate();

  createEffect(() => {
    if (!isAuthenticated() && !import.meta.env.VITE_DEV_USER_ID) {
      void navigate({ to: "/login" });
    }
  });

  const memberships = useQuery(() => ({
    queryKey: ["me", "workspaces"],
    queryFn: () => apiRequest<{ items: Item[] }>("/v1/me/workspaces"),
    enabled: isAuthenticated() || !!import.meta.env.VITE_DEV_USER_ID,
  }));

  return (
    <div class="min-h-screen bg-base-200">
      <div class="navbar bg-base-100 border-b border-base-300 px-6">
        <div class="flex-1 font-semibold">ScaiCRM</div>
        <div class="flex-none">
          <button class="btn btn-ghost btn-sm" onClick={() => logout()}>
            Sign out
          </button>
        </div>
      </div>
      <main class="max-w-4xl mx-auto p-8">
        <h1 class="text-2xl font-bold mb-2">Your workspaces</h1>
        <p class="opacity-70 mb-6">Pick a workspace to enter.</p>

        <Show when={memberships.isLoading}>
          <div class="text-center py-10 opacity-60">Loading…</div>
        </Show>
        <Show when={memberships.isError}>
          <div class="alert alert-error">
            Failed to load workspaces: {(memberships.error as Error)?.message}
          </div>
        </Show>

        <Show when={memberships.data}>
          {(data) => (
            <Show
              when={data().items.length > 0}
              fallback={
                <div class="card bg-base-100 shadow-sm">
                  <div class="card-body items-center text-center">
                    <p class="opacity-70">You have no workspace memberships yet.</p>
                    <p class="text-sm opacity-50">Ask a workspace admin to invite you.</p>
                  </div>
                </div>
              }
            >
              <ul class="flex flex-col gap-3">
                <For each={data().items}>
                  {(m) => (
                    <li>
                      <Link
                        to="/workspaces/$wsKey"
                        params={{ wsKey: m.workspace_key }}
                        class="card bg-base-100 shadow-sm hover:shadow-md transition"
                      >
                        <div class="card-body flex-row items-center justify-between">
                          <div>
                            <div class="text-lg font-semibold">{m.workspace_name}</div>
                            <div class="text-sm opacity-70">
                              {m.tenant_name} · key: <code>{m.workspace_key}</code>
                            </div>
                          </div>
                          <div class="flex gap-2 items-center">
                            <span class="badge badge-outline">{m.role_level}</span>
                            <Show when={m.read_only}>
                              <span class="badge badge-warning">read-only</span>
                            </Show>
                            <Show when={m.archived}>
                              <span class="badge badge-ghost">archived</span>
                            </Show>
                          </div>
                        </div>
                      </Link>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          )}
        </Show>
      </main>
    </div>
  );
}
